import "./App.css";
import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import QrReader from "react-web-qr-reader";
import { ListChecks, UserPlus, Search, X } from "lucide-react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { FixedSizeList as List } from 'react-window';

import createFuzzySearch from "@nozbe/microfuzz";
import staticGuestList from "./guests.json";

console.log("Loaded guest list:", staticGuestList); // Debug log

//https://lu.ma/check-in/evt-3yl2QtSZB5Xar02?pk=g-Bj74K4x4jgpM0Ck


const servers = [
  "https://cloud-function-checkin-1014793760790.us-south1.run.app",
  "https://cloud-function-checkin-1014793760790.us-east5.run.app", 
  "https://cloud-function-checkin-1014793760790.us-central1.run.app",
  "https://cloud-function-checkin-1014793760790.us-east4.run.app",
  "https://cloud-function-checkin-1014793760790.us-east1.run.app",
  "https://cloud-function-checkin-1014793760790.us-west4.run.app",
  "https://cloud-function-checkin-1014793760790.us-west1.run.app",
  "https://cloud-function-checkin-1014793760790.us-west3.run.app",
  "https://cloud-function-checkin-1014793760790.us-west2.run.app",
  "https://cloud-function-checkin-1014793760790.northamerica-northeast2.run.app",
  "https://cloud-function-checkin-1014793760790.northamerica-northeast1.run.app",
];


/*
const servers = [
  'http://localhost:8080'
];
*/

const fuzzySearch = createFuzzySearch(staticGuestList, {
  key: "name",
  strategy: "aggressive"
});

const App = () => {

  const [isOpen, setIsOpen] = React.useState(false);
  const [isDrawerLoading, setIsDrawerLoading] = useState(true);
  
  const toggleDrawer = () => {
    if (!isOpen) {
      setIsDrawerLoading(true);
      // Reset loading state immediately when opening
      setTimeout(() => setIsDrawerLoading(false), 0);
    }
    setIsOpen((prevState) => !prevState);
  };
  
  const [isManualDrawerOpen, setIsManualDrawerOpen] = React.useState(false);
  const toggleManualDrawer = () => {
    setIsManualDrawerOpen((prevState) => !prevState);
  };
  
  const [manualName, setManualName] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualCheckingIn, setManualCheckingIn] = useState(false);
  
  const [search, setSearch] = useState("");
  const searchTimeoutRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  const [guestList, setGuestList] = useState([]);
  const [scanned, setScanned] = useState(false);
  const [url, setUrl] = useState("");
  const [scanStatus, setScanStatus] = useState("Scanning..."); // Added state for scan status
  const [scanBgColor, setScanBgColor] = useState("black"); // Added state for scan background color
  const [lastScannedCode, setLastScannedCode] = useState(""); // Track last scanned code
  
  // Long press state
  const [longPressItem, setLongPressItem] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [lastTapTime, setLastTapTime] = useState(0);
  const [lastTapIndex, setLastTapIndex] = useState(null);
  const doubleTapDelay = 300; // ms between taps

  const [isOffline, setIsOffline] = useState(false);
  const [offlineCheckins, setOfflineCheckins] = useState(() => {
    const saved = localStorage.getItem('offlineCheckins');
    return saved ? JSON.parse(saved) : [];
  });

  // Debounce search input
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearch(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 150); // 150ms delay
  }, []);

  // Memoize search results
  const searchResults = useMemo(() => {
    if (debouncedSearch === "") return [];
    return fuzzySearch(debouncedSearch);
  }, [debouncedSearch]);

  // Memoize the status badge component
  const StatusBadge = useMemo(() => {
    const Badge = ({ status }) => (
      <div
        className={`transition-all space-y-1 self-center ml-auto inline-flex items-center rounded-md border px-2.5 py-0.5 text-sm font-semibold ${
          status === "approved"
            ? "text-[#96ffc2] bg-[#0699599c] border-[#07a46055]"
            : status === "waitlist"
            ? "text-[#9091ff] bg-[#0736a1a3] border-[#0712a455]"
            : status === "invited"
            ? "text-[#ff90f9] bg-[#a10785a3] border-[#0712a455]"
            : "text-[#ff8b8b] bg-[#a20505a3] border-[#a4079455]"
        }`}
      >
        {status === "approved" ? "Click To Check-in" :
         status.charAt(0).toUpperCase() + status.slice(1)}
      </div>
    );
    return React.memo(Badge);
  }, []);

  // Process offline checkins when back online
  const processOfflineCheckins = useCallback(async () => {
    const checkins = [...offlineCheckins];
    setOfflineCheckins([]); // Clear the queue
    localStorage.removeItem('offlineCheckins');
    
    // Update existing offline check-ins in the guest list to show they're being processed
    setGuestList(prevList => {
      return prevList.map(entry => {
        if (entry.isOfflineCheckin) {
          return {
            ...entry,
            isProcessing: true
          };
        }
        return entry;
      });
    });
    
    for (const checkin of checkins) {
      try {
        const data = await fetch(
          servers[Math.floor(Math.random() * servers.length)],
          {
            method: "POST",
            body: JSON.stringify({ 
              code: checkin.data,
              operation: checkin.operation 
            })
          }
        );

        const json = await data.json();
        
        // Update the guest list to reflect the server response
        setGuestList(prevList => {
          return prevList.map(entry => {
            if (entry.guest.proxy_key === checkin.guest.proxy_key && entry.isOfflineCheckin) {
              return {
                ...json,
                isOfflineCheckin: false,
                isProcessing: false
              };
            }
            return entry;
          });
        });
      } catch (error) {
        console.error('Error processing offline checkin:', error);
        // If processing fails, keep the check-in as offline
        setOfflineCheckins(prev => [...prev, checkin]);
      }
    }
  }, [offlineCheckins]);

  // Add connection check function
  const checkConnection = useCallback(async () => {
    try {
      const response = await fetch(servers[0], {
        method: 'HEAD',
        timeout: 5000
      });
      console.log("Response:", response.status);
      if (response.status === 500 && isOffline) {
        console.log("Connection restored, processing offline checkins");
        setIsOffline(false);
        setScanStatus("Processing offline check-ins...");
        setScanBgColor("grey");
        // Process queued checkins
        await processOfflineCheckins();
        setScanStatus("Scanning...");
        setScanBgColor("black");
      }
    } catch (error) {
      if (!isOffline) {
        setIsOffline(true);
        setScanStatus("Offline Mode Active");
        setScanBgColor("#500000"); // dark red
      }
    }
  }, [isOffline, processOfflineCheckins]);

  // Set up connection check interval
  useEffect(() => {
    const interval = setInterval(checkConnection, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [checkConnection]);

  // Save offline checkins to localStorage when updated
  useEffect(() => {
    if (offlineCheckins.length > 0) {
      localStorage.setItem('offlineCheckins', JSON.stringify(offlineCheckins));
    }
  }, [offlineCheckins]);

  const handleScan = useCallback(async (result, operation = "checkin-guest") => { 
    if (!result || !result.data) return;
    if (result.data === "") return;

    // Check if this is the same code as last scan
    if (result.data === lastScannedCode) {
      console.log("Duplicate scan detected, ignoring");
      return;
    }

    if (isOpen) toggleDrawer();
    if (isManualDrawerOpen) toggleManualDrawer();

    // check if https://lu.ma/check-in/evt-RSyFL5DkBWknILG?pk= is in the url, add if not
    if (!result.data.includes("https://lu.ma/check-in/evt-3yl2QtSZB5Xar02?pk=")) {
      result.data = "https://lu.ma/check-in/evt-3yl2QtSZB5Xar02?pk=" + result.data;
    }

    if (result.data && !scanned) {
      setUrl(result.data);
      setScanned(true);

      console.log("Result:", result);

      // Extract proxy_key from the URL
      const proxyKey = result.data.split("pk=")[1];
      
      // Find guest in local list
      const localGuest = staticGuestList.find(guest => guest.proxy_key === proxyKey);
      console.log("Local guest:", localGuest);
      console.log("Local guest approval status:", localGuest?.approval_status);
      
      // Check local list first
      if (localGuest && localGuest.approval_status === "approved") {
        // Update last scanned code
        setLastScannedCode(result.data);
        
        // Add local check-in entry immediately
        const localEntry = {
          status: "success",
          guest: {
            name: localGuest.name,
            email: localGuest.email,
            proxy_key: localGuest.proxy_key
          },
          isLocalOnly: true,
          isOfflineCheckin: isOffline
        };
        
        setGuestList(prev => [localEntry, ...prev]);
        setScanStatus("Guest checked in locally");
        setScanBgColor("green");
        
        // Reset scanned state immediately to allow next scan
        setTimeout(() => {
          setScanned(false);
          setScanStatus(isOffline ? "Offline Mode Active" : "Scanning...");
          setScanBgColor(isOffline ? "#500000" : "black");
        }, 2000); // Short delay just to show the success message

        // If offline, queue the check-in
        if (isOffline) {
          const offlineEntry = {
            data: result.data,
            operation: operation,
            timestamp: Date.now(),
            guest: localGuest
          };
          setOfflineCheckins(prev => [...prev, offlineEntry]);
          return;
        }

        // Make server request in the background for tracking
        try {
          fetch(
            servers[Math.floor(Math.random() * servers.length)],
            {
              method: "POST",
              body: JSON.stringify({ 
                code: result.data,
                operation: operation 
              })
            }
          )
          .then(data => data.json())
          .then(json => {
            // Update the guest list entry with server response
            setGuestList(prevList => {
              return prevList.map(entry => {
                if (entry.guest.proxy_key === localGuest.proxy_key && entry.isLocalOnly) {
                  return {
                    ...json,
                    isLocalOnly: false
                  };
                }
                return entry;
              });
            });
          })
          .catch(error => {
            console.error("Background check-in error:", error);
            // If server request fails, add to offline queue
            if (!isOffline) {
              const offlineEntry = {
                data: result.data,
                operation: operation,
                timestamp: Date.now(),
                guest: localGuest
              };
              setOfflineCheckins(prev => [...prev, offlineEntry]);
            }
          });
        } catch (error) {
          console.error("Background check-in error:", error);
        }
        return; // Return early since we've handled the immediate response
      }

      // If offline and guest is not approved locally, show error
      if (isOffline) {
        setScanStatus("Guest not approved");
        setScanBgColor("red");
        setTimeout(() => {
          setScanned(false);
          setScanStatus("Offline Mode Active");
          setScanBgColor("#500000");
        }, 2000);
        return;
      }

      // For non-approved or non-local guests, proceed with server request
      try {
        // Make the server request with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        const data = await fetch(
          servers[Math.floor(Math.random() * servers.length)],
          {
            method: "POST",
            body: JSON.stringify({ 
              code: result.data,
              operation: operation 
            }),
            signal: controller.signal
          }
        );

        clearTimeout(timeoutId);

        const json = await data.json();

        if (json.status === "guest_not_found") {
          json.guest = {
            name: "Guest not found",
            email: "Guest not found",
          };
        }

        if (json.guest.name === null) {
          json.guest.name = "Anonymous";
        }

        // Update guest list based on server response
        setGuestList(prev => {
          const updatedList = [...prev];
          
          // Find if we have a local entry for this guest
          const localIndex = updatedList.findIndex(
            entry => entry.guest.proxy_key === json.guest.proxy_key && entry.isLocalOnly
          );
          
          // Then check for any existing entries
          const existingIndex = localIndex === -1 ? 
            updatedList.findIndex(entry => 
              entry.guest.proxy_key === json.guest.proxy_key || 
              entry.guest.email === json.guest.email
            ) : localIndex;
          
          if (existingIndex !== -1) {
            // Update the existing entry with server response
            updatedList[existingIndex] = {
              ...json,
              isLocalOnly: false
            };
          } else {
            // Only add new entry if it doesn't exist at all
            updatedList.unshift(json);
          }
          
          return updatedList;
        });

        // Show appropriate status message based on server response
        if (json.status === "success") {
          setLastScannedCode(result.data); // Update last scanned code for server success
          setScanStatus("Guest checked in");
          setScanBgColor("green");
        } else if (json.status === "already-checked-in") {
          setLastScannedCode(result.data); // Update last scanned code for already checked in
          setScanStatus("Guest already scanned");
          setScanBgColor("orange");
        } else {
          setScanStatus("Guest not found");
          setScanBgColor("red");
        }

        setTimeout(() => {
          setScanned(false);
          setScanStatus("Scanning...");
          setScanBgColor("black");
        }, 2000);
      } catch (error) {
        console.error("Check-in error:", error);
        
        // If error is timeout or network related, switch to offline mode
        if (error.name === 'AbortError' || !navigator.onLine) {
          setIsOffline(true);
          setScanStatus("Switching to Offline Mode");
          setScanBgColor("#500000");
          
          // If guest was approved locally, add to offline queue
          if (localGuest && localGuest.approval_status === "approved") {
            const offlineEntry = {
              data: result.data,
              operation: operation,
              timestamp: Date.now(),
              guest: localGuest
            };
            setOfflineCheckins(prev => [...prev, offlineEntry]);
          }
        } else {
          setScanStatus("Error during check-in");
          setScanBgColor("red");
        }
        
        setTimeout(() => {
          setScanned(false);
          setScanStatus(isOffline ? "Offline Mode Active" : "Scanning...");
          setScanBgColor(isOffline ? "#500000" : "black");
        }, 2000);
      }
    }
  }, [isOpen, isManualDrawerOpen, toggleDrawer, toggleManualDrawer, scanned, url, isOffline, lastScannedCode]);

  const handleManualCheckIn = async () => {
    if (!manualName || !manualEmail) {
      setScanStatus("Name and email required");
      setScanBgColor("red");
      setTimeout(() => {
        setScanStatus("Scanning...");
        setScanBgColor("black");
      }, 2000);
      return;
    }

    setManualCheckingIn(true);
    setScanStatus("Registering guest...");
    setScanBgColor("grey");

    try {
      // Call the server to register the user
      const response = await fetch(
        servers[Math.floor(Math.random() * servers.length)],
        {
          method: "POST",
          body: JSON.stringify({
            operation: "manual-add",
            name: manualName,
            email: manualEmail,
            event_api_id: "evt-3yl2QtSZB5Xar02"
          }),
        }
      );

      const json = await response.json();
      
      if (json.status === "success") {
        // Add the manually registered guest to the list with special properties
        setGuestList((prev) => {
          return [{
            status: "manual",
            guest: {
              name: manualName,
              email: manualEmail,
              guestId: json.guestId
            },
            isManual: true,
            currentCount: json.currentCount
          }, ...prev];
        });
        
        // Show success message
        setScanStatus("Guest manually registered");
        setScanBgColor("green");
        
        // Clear the form
        setManualName("");
        setManualEmail("");
        toggleManualDrawer();
      } else {
        setScanStatus(json.message || "Registration failed");
        setScanBgColor("red");
      }
    } catch (error) {
      console.error("Manual registration error:", error);
      setScanStatus("Error during registration");
      setScanBgColor("red");
    }

    setManualCheckingIn(false);
    
    // Reset status after a delay
    setTimeout(() => {
      setScanStatus("Scanning...");
      setScanBgColor("black");
    }, 2000);
  };

  const handleError = (error) => {
    console.log(error);
  };

  const handleDoubleTap = (entry, index) => {
    // Don't allow double tap for manual entries
    if (entry.isManual) return;
    
    // Allow double tap for both checked-in and already-checked-in guests
    if (entry.status !== "success" && entry.status !== "already-checked-in") return;

    const now = Date.now();
    if (lastTapIndex === index && now - lastTapTime < doubleTapDelay) {
      // Double tap detected
      setSelectedGuest(entry);
      setShowConfirmModal(true);
      // Reset tap state
      setLastTapTime(0);
      setLastTapIndex(null);
    } else {
      // First tap
      setLastTapTime(now);
      setLastTapIndex(index);
    }
  };

  const handleUncheckConfirm = () => {
    const guestToUncheck = selectedGuest;
    
    // Close the modal first for better UX
    setShowConfirmModal(false);
    setSelectedGuest(null);
    setLongPressItem(null);
    
    // Then perform the uncheck operation using email instead of proxy_key
    if (guestToUncheck && guestToUncheck.guest && guestToUncheck.guest.email) {
      // Call the server to uncheck the guest by email
      fetch(
        servers[Math.floor(Math.random() * servers.length)],
        {
          method: "POST",
          body: JSON.stringify({
            operation: "uncheck-guest",
            email: guestToUncheck.guest.email
          }),
        }
      )
      .then(response => {
        // Check if HTTP status is 200 (success)
        const isSuccess = response.status === 200;
        return response.json().then(data => {
          return { data, isSuccess };
        });
      })
      .then(({ data: json, isSuccess }) => {
        if (isSuccess) {
          // Update the existing entry in the guest list
          setGuestList(prevList => {
            return prevList.map(item => {
              if (item.guest.email === guestToUncheck.guest.email) {
                // Keep the original guest data but update the status to unchecked
                return { 
                  ...item, 
                  status: "unchecked",
                  // Reset the url to allow re-checking in
                  url: ""
                };
              }
              return item;
            });
          });
          
          // Reset the url state to allow re-scanning the same QR code
          setUrl("");
          
          // Show success message
          setScanStatus("Guest unchecked");
          setScanBgColor("green");
        } else {
          setScanStatus(json.message || "Unchecking failed");
          setScanBgColor("red");
        }
        
        // Reset status after a delay
        setTimeout(() => {
          setScanStatus("Scanning...");
          setScanBgColor("black");
        }, 2000);
      })
      .catch(error => {
        console.error("Unchecking error:", error);
        setScanStatus("Error during uncheck");
        setScanBgColor("red");
        
        // Reset status after a delay
        setTimeout(() => {
          setScanStatus("Scanning...");
          setScanBgColor("black");
        }, 2000);
      });
    }
  };

  const handleUncheckCancel = () => {
    setShowConfirmModal(false);
    setSelectedGuest(null);
    setLongPressItem(null);
  };

  // Pre-render the static list for better performance
  const ListRow = useCallback(({ index, style }) => {
    const entry = staticGuestList[index];
    return (
      <div style={style}>
        <div
          onClick={() => handleScan({ data: entry.proxy_key })}
          className="flex py-3 text-white hover:bg-[#2a2a2a] transition-colors"
        >
          <div className="space-y-1">
            <p className="text-sm text-white font-medium leading-none">
              {entry.name.length > 30
                ? `${entry.name.substring(0, 30)}...`
                : entry.name}
            </p>
            <p className="text-sm text-gray-500">
              {entry.email.length > 30
                ? `${entry.email.substring(0, 30)}...`
                : entry.email}
            </p>
          </div>
          <StatusBadge status={entry.approval_status} />
        </div>
      </div>
    );
  }, [handleScan, StatusBadge]);

  const SearchListRow = useCallback(({ index, style }) => {
    const entry = searchResults[index];
    return (
      <div style={style}>
        <div
          onClick={() => handleScan({ data: entry.item.proxy_key })}
          className="flex py-3 text-white hover:bg-[#2a2a2a] transition-colors"
        >
          <div className="space-y-1">
            <p className="text-sm text-white font-medium leading-none">
              {entry.item.name.length > 30
                ? `${entry.item.name.substring(0, 30)}...`
                : entry.item.name}
            </p>
            <p className="text-sm text-gray-500">
              {entry.item.email.length > 30
                ? `${entry.item.email.substring(0, 30)}...`
                : entry.item.email}
            </p>
          </div>
          <StatusBadge status={entry.item.approval_status} />
        </div>
      </div>
    );
  }, [handleScan, StatusBadge]);

  const listItems = guestList.map((entry, i) => {
    return (
      <div key={i} className="relative">
        <div 
          className="flex py-3 px-2 text-white bg-[#1a1a1a]"
          onClick={() => handleDoubleTap(entry, i)}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <div className="space-y-1">
            <p className="text-sm text-white font-medium leading-none select-none">
              {entry.guest.name.length > 30
                ? `${entry.guest.name.substring(0, 20)}...`
                : entry.guest.name}
            </p>
            <p className="text-sm text-gray-500 select-none">
              {entry.guest.email.length > 20
                ? `${entry.guest.email.substring(0, 20)}...`
                : entry.guest.email}
            </p>
          </div>

          <div
            className={`select-none transition-all space-y-1 self-center ml-auto inline-flex items-center rounded-md border px-2.5 py-0.5 text-sm font-semibold ${
              entry.isProcessing
                ? "text-gray-300 bg-[#4a4a4a] border-[#5a5a5a55]" // Processing entries get a neutral gray color
                : entry.isManual
                ? "text-[#ffd700] bg-[#8b7355] border-[#daa52055]"
                : entry.status === "success" && entry.isOfflineCheckin
                ? "text-[#ff9696] bg-[#990606] border-[#a40707]"
                : entry.status === "success"
                ? "text-[#96ffc2] bg-[#0699599c] border-[#07a46055]"
                : entry.status === "already-checked-in"
                ? "text-[#9091ff] bg-[#0736a1a3] border-[#0712a455]"
                : entry.status === "unchecked"
                ? "text-gray-400 bg-[#3a3a3a] border-[#5a5a5a55]"
                : "text-[#ff8b8b] bg-[#a20505a3] border-[#a4070755]"
            }`}
          >
            {entry.isProcessing
              ? "Processing..."
              : entry.isManual
              ? "Manual Entry"
              : entry.status === "success" && entry.isOfflineCheckin
              ? "Checked In Offline"
              : entry.status === "success"
              ? "Checked In"
              : entry.status === "already-checked-in"
              ? "Already Checked In"
              : entry.status === "unchecked"
              ? "Unchecked"
              : "Guest Not Found"}
          </div>
        </div>
      </div>
    );
  });
  
  // Add copy offline checkins button component
  const CopyOfflineButton = () => {
    if (!isOffline && offlineCheckins.length === 0) return null;

    const handleCopy = () => {
      const checkinData = offlineCheckins.map(entry => entry.guest.proxy_key).join('\n');
      navigator.clipboard.writeText(checkinData);
      setScanStatus("Offline checkins copied!");
      setTimeout(() => {
        setScanStatus("Offline Mode Active");
      }, 2000);
    };

    return (
      <div className="absolute bottom-5 left-20 w-12 h-12 z-50">
        <div
          onClick={handleCopy}
          className="rounded-full overflow-hidden w-12 h-12 bg-[#500000] flex items-center justify-center hover:bg-[#400000]"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="text-white text-2xl font-bold" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full max-h-lvh overflow-hidden items-center bg-black">
      {isOffline && (
        <div className="absolute top-0 left-0 right-0 bg-[#500000] text-white font-bold py-1 text-center">
          OFFLINE MODE
        </div>
      )}
      <CopyOfflineButton />
      <div className="w-full flex-grow order-1">
        <QrReader
          delay={0}
          onError={handleError}
          onScan={handleScan}
          className="flex items-center justify-center"
        />

        <div className={"m-2"}>
          <div
            className={`w-full space-y-1 self-center ml-auto inline-flex items-center rounded-md border px-4 py-4 text-sm font-semibold ${
              scanBgColor === "green"
                ? "text-[#96ffc2] bg-[#0699599c] border-[#07a46055]"
                : scanBgColor === "orange"
                ? "text-[#9091ff] bg-[#0736a1a3] border-[#0712a455]"
                : scanBgColor === "red"
                ? "text-[#ff8b8b] bg-[#a20505a3] border-[#a4070755]"
                : scanBgColor === "grey"
                ? "text-[#ffffff] bg-[#e4e3e355] border-[#e0e0e055]"
                : "text-[#c7c7c7] bg-[#77777755] border-[#72727255]"
            }`}
          >
            <div className="text-center m-auto">{scanStatus}</div>
          </div>
        </div>
      </div>

      <div className="w-full max-h-[25vh] max-w-full overflow-y-auto p-2 order-2 scan-history border-t border-gray-800">
        <div className="text-white font-bold py-2 px-1 flex items-center justify-between">
          <span>Scan history</span>
          <div className="flex items-center">
            {guestList.length > 0 && guestList.some(entry => entry.status === "success" || entry.status === "already-checked-in") && (
              <span className="text-xs text-gray-400 mr-2">Double tap to uncheck</span>
            )}
            {guestList.length > 0 && (
              <span className="text-xs text-gray-400">{guestList.length} {guestList.length === 1 ? 'entry' : 'entries'}</span>
            )}
          </div>
        </div>

        <div className="divide-y divide-gray-800">
          {listItems}
        </div>
      </div>
      
      {/* Guest List Button */}
      <div className="absolute bottom-5 left-5 w-12 h-12 z-50">
        <div
          onClick={toggleDrawer}
          className="rounded-full overflow-hidden w-12 h-12 bg-[#77777755] flex items-center justify-center"
        >
          <ListChecks className="text-white text-2xl font-bold" />
        </div>
      </div>
      
      {/* Manual Check-in Button */}
      <div className="absolute bottom-5 right-5 w-12 h-12 z-50">
        <div
          onClick={toggleManualDrawer}
          className="rounded-full overflow-hidden w-12 h-12 bg-[#77777755] flex items-center justify-center"
        >
          <UserPlus className="text-white text-2xl font-bold" />
        </div>
      </div>
      
      {/* Confirmation Modal */}
      {showConfirmModal && selectedGuest && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-lg p-5 max-w-sm w-full select-none">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-bold text-lg select-none">Uncheck Guest?</h3>
              <button 
                onClick={handleUncheckCancel}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            
            <p className="text-white mb-2 select-none">
              Are you sure you want to uncheck:
            </p>
            <p className="text-white font-bold mb-4 select-none">
              {selectedGuest.guest.name}
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleUncheckCancel}
                className="flex-1 py-2 rounded-md border border-gray-600 text-white select-none"
              >
                Cancel
              </button>
              <button
                onClick={handleUncheckConfirm}
                className="flex-1 py-2 rounded-md bg-red-600 text-white select-none"
              >
                Uncheck
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Guest List Drawer */}
      <Drawer
        open={isOpen}
        onClose={toggleDrawer}
        direction="bottom"
        className="drawer-container"
        size={600}
        duration={50}
        style={{ backgroundColor: "#1a1a1a", borderRadius: "10px" }}
        onTransitionEnd={() => {
          console.log("Drawer transition ended, setting loading to false"); // Debug log
          setIsDrawerLoading(false);
        }}
      >
        <div className="w-full h-full flex flex-col">
          <div className="sticky top-0 z-10 p-4 bg-[#1a1a1a] border-b border-gray-800">
            <div className="flex justify-between items-center pb-2">
              <div className="text-white font-bold">Search</div>
              <button 
                onClick={toggleDrawer}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            <input
              type="search"
              placeholder="Search..."
              value={search}
              className="text-white text-md p-2 flex h-12 w-full rounded-md border border-[#7b7b7b] bg-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-[#7b7b7b] focus:border-transparent"
              onChange={handleSearchChange}
            />
          </div>

          <div className="flex-1 overflow-hidden scan-history">
            <div className="h-full px-4">
              {console.log("Rendering drawer content, search:", search, "isDrawerLoading:", isDrawerLoading, "staticGuestList length:", staticGuestList.length)}
              {search === "" ? (
                <List
                  height={window.innerHeight * 0.8}
                  itemCount={staticGuestList.length}
                  itemSize={72}
                  width="100%"
                  className="divide-y divide-gray-800"
                >
                  {ListRow}
                </List>
              ) : (
                <List
                  height={window.innerHeight * 0.8}
                  itemCount={searchResults.length}
                  itemSize={72}
                  width="100%"
                  className="divide-y divide-gray-800"
                >
                  {SearchListRow}
                </List>
              )}
            </div>
          </div>
        </div>
      </Drawer>
      
      {/* Manual Check-in Drawer */}
      <Drawer
        open={isManualDrawerOpen}
        onClose={toggleManualDrawer}
        direction="bottom"
        className="drawer-container"
        size={400}
        duration={100}
        style={{ backgroundColor: "#1a1a1a", borderRadius: "10px" }}
      >
        <div className="w-full h-full flex flex-col p-4">
          <div className="flex justify-between items-center mb-4">
            <div className="text-white font-bold text-xl">Add Guest</div>
            <button 
              onClick={toggleManualDrawer}
              className="text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="mb-4">
            <label className="block text-white text-sm font-bold mb-2">
              Name
            </label>
            <input
              type="text"
              placeholder="Guest Name"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              className="text-white text-md p-2 flex h-12 w-full rounded-md border border-[#7b7b7b] bg-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-[#7b7b7b] focus:border-transparent"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-white text-sm font-bold mb-2">
              Email
            </label>
            <input
              type="email"
              placeholder="guest@example.com"
              value={manualEmail}
              onChange={(e) => setManualEmail(e.target.value)}
              className="text-white text-md p-2 flex h-12 w-full rounded-md border border-[#7b7b7b] bg-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-[#7b7b7b] focus:border-transparent"
            />
          </div>
          
          <button
            onClick={handleManualCheckIn}
            disabled={manualCheckingIn}
            className={`w-full py-3 rounded-md font-bold text-white ${
              manualCheckingIn 
                ? "bg-gray-600" 
                : "bg-[#0699599c] hover:bg-[#06995980]"
            }`}
          >
            {manualCheckingIn ? "Processing..." : "Add Guest"}
          </button>
        </div>
      </Drawer>
    </div>
  );
};

export default App;
